import { updateEstimate } from './estimateService';
import { versionDetector } from './estimationVersionDetector';
import { legacyAdapter } from './legacyEstimationAdapter';
import { calculationService } from './calculationService';

export class EstimationMigrationService {
  private static instance: EstimationMigrationService;

  private constructor() {}

  public static getInstance(): EstimationMigrationService {
    if (!EstimationMigrationService.instance) {
      EstimationMigrationService.instance = new EstimationMigrationService();
    }
    return EstimationMigrationService.instance;
  }

  public recalculatePrice(estimateData: any): number | null {
    try {
      if (!versionDetector.isLegacyFormat(estimateData)) {
        return estimateData.priceRange?.min || estimateData.priceRange?.max || null;
      }

      const adaptedData = legacyAdapter.adaptLegacyEstimation(estimateData);

      const departmentCode = adaptedData.description?.location?.postcode?.substring(0, 2);
      const result = calculationService.calculateEstimate(adaptedData, departmentCode);

      return result.totalTTC;
    } catch (error) {
      console.error('Error recalculating price:', error);
      return null;
    }
  }

  public async migrateAndSave(
    userId: string,
    estimateId: string,
    estimateData: any
  ): Promise<void> {
    try {
      if (!versionDetector.isLegacyFormat(estimateData)) {
        console.log('Estimation already in current format, no migration needed');
        return;
      }

      console.log('🔄 Migrating estimation to current format...');

      const adaptedData = legacyAdapter.adaptLegacyEstimation(estimateData);

      const recalculatedPrice = this.recalculatePrice(estimateData);

      const cleanedData = legacyAdapter.cleanupLegacyFields({
        ...estimateData,
        ...adaptedData,
        formatVersion: 'v2',
        migratedAt: new Date().toISOString(),
        priceRange: recalculatedPrice ? {
          min: recalculatedPrice,
          max: recalculatedPrice
        } : estimateData.priceRange
      });

      await updateEstimate(userId, estimateId, cleanedData);

      console.log('✅ Estimation successfully migrated and saved to Firebase');
    } catch (error) {
      console.error('❌ Error during migration:', error);
      throw error;
    }
  }

  public async shouldAutoMigrate(estimateData: any): boolean {
    if (!versionDetector.isLegacyFormat(estimateData)) {
      return false;
    }

    const hasBeenMigrated = estimateData.formatVersion === 'v2' ||
                           estimateData.migratedAt !== undefined;

    return !hasBeenMigrated;
  }

  public detectEstimationType(estimateData: any): 'quick' | 'detailed' {
    if (estimateData.type) {
      return estimateData.type;
    }

    const detailedCategories = [
      'preparation', 'demolition', 'gros-oeuvre', 'vrd',
      'isolation', 'cloisons', 'technique', 'sanitaire',
      'revetements', 'menuiserie', 'finitions', 'exterieurs'
    ];

    const hasDetailedCategories = detailedCategories.some(
      category => estimateData[category] !== undefined
    );

    if (hasDetailedCategories) {
      return 'detailed';
    }

    const hasQuickFields =
      estimateData.propertyType !== undefined &&
      estimateData.area !== undefined &&
      estimateData.renovationLevel !== undefined;

    return hasQuickFields ? 'quick' : 'detailed';
  }

  /**
   * Migre les escaliers de la section cloisons vers grosoeuvre
   * Pour les estimations créées avant la réorganisation
   */
  private migrateStairsFromCloisonsToGrosoeuvre(estimateData: any): any {
    // Si pas de données de cloisons avec escaliers, rien à faire
    if (!estimateData.cloisons?.escaliers) {
      return estimateData;
    }

    console.log('🔄 Migration des escaliers de cloisons vers grosoeuvre');

    const cloisonsStairs = estimateData.cloisons.escaliers;
    const result = { ...estimateData };

    // Initialiser la section stairs dans grosoeuvre si nécessaire
    if (!result.grosoeuvre) {
      result.grosoeuvre = {};
    }
    if (!result.grosoeuvre.stairs) {
      result.grosoeuvre.stairs = {};
    }

    // Mapper les anciennes clés vers les nouvelles
    const stairsMigrationMap: Record<string, string> = {
      'bois': 'bois_permarche',
      'beton': 'beton_permarche',
      'metal': 'metal_permarche',
      'gardeCorps': 'gardecorps_interieur'
    };

    // Migrer chaque item d'escalier
    Object.keys(stairsMigrationMap).forEach(oldKey => {
      const newKey = stairsMigrationMap[oldKey];
      if (cloisonsStairs[oldKey]) {
        result.grosoeuvre.stairs[newKey] = cloisonsStairs[oldKey];
        console.log(`  ✓ Migré ${oldKey} -> ${newKey}`);
      }
    });

    // Supprimer la section escaliers de cloisons
    const { escaliers, ...restCloisons } = result.cloisons;
    result.cloisons = restCloisons;

    return result;
  }

  public prepareForDisplay(estimateData: any): any {
    const detectedType = this.detectEstimationType(estimateData);

    console.log('🔍 prepareForDisplay - Type détecté:', {
      originalType: estimateData.type,
      detectedType,
      hasPropertyType: !!estimateData.propertyType,
      hasDetailedCategories: ['preparation', 'demolition'].some(c => estimateData[c])
    });

    let baseData = {
      ...estimateData,
      type: detectedType
    };

    // Appliquer la migration des escaliers si nécessaire
    baseData = this.migrateStairsFromCloisonsToGrosoeuvre(baseData);

    if (detectedType === 'quick') {
      console.log('🔍 Quick estimation - preserving original data');
      const cleaned = { ...baseData };
      if (cleaned.priceRange?.sections) {
        delete cleaned.priceRange.sections;
      }
      return cleaned;
    }

    if (detectedType === 'detailed' && versionDetector.isLegacyDetailedEstimate(estimateData)) {
      console.log('🔍 Detailed legacy estimation - applying adaptation');
      const adapted = legacyAdapter.adaptLegacyEstimation(estimateData);

      return {
        ...baseData,
        ...adapted,
        _isLegacy: true,
        _needsMigration: true
      };
    }

    return baseData;
  }
}

export const migrationService = EstimationMigrationService.getInstance();
